import * as THREE from 'three';
import { requestRender } from '../state/bus.js';
import { S } from '../state/store.js';
import { sourceTime } from '../core/video-time.js';
import { getBlob } from '../services/media-store.js';
import { toast } from '../ui/toast.js';

/* DECODING CLIPS — the only place in the app that turns an MP4 into pixels.

   Two ways in, because an export knows every instant it wants before it
   starts and nothing else does:

     export             canvasesAtTimestamps(array) — the whole list up front,
                        so each packet is decoded at most once. It CANNOT be
                        fed lazily: handed a pull-one-await-one iterator it
                        deadlocks, since nothing reaches its sample queue
                        until the key packet changes. Verified against the
                        library before this was built on it.
     everything else    getCanvas(t) — one arbitrary instant, ~2.4ms measured.
                        Scrubbing, playback and the still export all use it.

   PLAYBACK READS AHEAD TOO, and that is a reversal. A streaming generator for
   playback existed here once and was removed, on the grounds that getCanvas was
   2.4ms against a 33ms budget and the pipelining solved nothing. Re-measured
   against real files, that number is not what getCanvas costs:

     1080p H.264, per frame     getCanvas 28.5ms    batch  1.9ms
     1080p VP8+alpha, per frame getCanvas  240ms    batch 11.9ms

   getCanvas re-seeks and replays the GOP every call — random access costs the
   same as sequential, which is the proof. So the old premise was false and the
   old objection was true: a look-ahead cannot rewind. This one is bounded, and
   has ONE rule for when it may be used:

       the clip is PLAYING, and the instant asked for lies forward of where the
       look-ahead has reached, inside the window it was opened over.

   A scrub, a loop wrap, an export opening, or the window running out fails that
   rule, closes the window, and falls back to getCanvas for that frame. It never
   tries to rewind, so it cannot get rewinding wrong. Paused, nothing reads
   ahead at all: the next instant is unknowable and a window would be a guess.

   Both share ONE retrieval rule ("the last frame in presentation order whose
   start timestamp is <= the one asked for"), so they cannot disagree about
   which frame belongs at a given instant.

   WHY NOT A <video> ELEMENT. `video.currentTime = t` seeks asynchronously to
   an implementation-defined frame, and THREE.VideoTexture uploads whatever
   the element happens to be showing when render() runs. There is no moment at
   which you can assert "this texture holds the frame for scene time t" — and
   that assertion is the entire feature, because the preview is meant to be a
   preview OF THE EXPORT. A hybrid (element for preview, decoder for export)
   is worse than either: two answers to one question, and the wrong one is the
   one nobody is looking at.

   WHY THE READERS ARE KEYED BY OBJECT ID, not held on the node. Dragging the
   width slider calls syncNode on every pointermove (see rail-right.js's
   remat), which rebuilds the node from scratch. A decoder torn down and
   rebuilt sixty times a second is unusable, and every rebuild would flash
   black while the first frame decoded. The registry outlives the node; the
   builder just re-attaches the texture that is already there. This is the
   same reasoning that makes buildImage deliberately leave its texture out of
   userData.texs, one step further.

   BYTES ARE FETCHED ONCE, into a Blob, and decoded from there. Never a URL:
   services/assets.js signs asset URLs for 8 hours on the assumption that
   "textures and models are decoded once and never re-fetched", and a decoder
   that reads byte ranges lazily for the life of the tab would quietly break
   that. One fetch at open time makes expiry structurally impossible. */

/* Each CanvasSink holds a hardware decoder. A handful is fine; a scene full
   of them will start failing to construct, and the failure surfaces as a
   blank plane rather than an error, so cap it where it can still be
   explained. */
export const MAX_VIDEOS = 4;

/* One sink resolution, used by the preview AND the export. The tempting
   optimisation — decode small for the viewport, full-size for the
   deliverable — would put the preview and the export on different pixels,
   which is the one thing export/frame.js exists to prevent. 1080p is the
   ceiling because that is already more than any plane in a preview needs and
   more than most signage sources carry. */
const MAX_DECODE_W = 1920;

let mb = null;
/* Lazily imported, exactly as export/mp4.js does it: mediabunny is a large
   dependency and a project with no video in it should never pay for it.
   Naming the formats rather than ALL_FORMATS keeps the Ogg, HLS and MPEG-TS
   demuxers out of the bundle; Matroska is in now because that is where a clip
   with an alpha channel lives (H.264 has none, VP8/VP9 in WebM does).

   Exported because it is the ONE place the library is loaded and has to stay
   that way: import it a second time by any other specifier and there are two
   copies in the page, which Mediabunny notices and warns about, and which
   would give the decoder and the muxer different registries to disagree in. */
export async function lib() {
  if (!mb) mb = await import('mediabunny');
  return mb;
}

/* Blobs by content id, so two objects using the same clip share one fetch and
   one parse. Not evicted: the same argument as textures.js's cache — a
   rebuild, an undo, or a re-add of the same file must not re-download. */
const blobs = new Map();

/**
 * Hand the reader bytes it already has, before anything asks for them.
 *
 * The importer is holding the File the moment it computes the content id, so
 * the first decode has no reason to go back to storage for the same bytes —
 * and going back is what made the FIRST import of a file race the write that
 * had only just been issued. Priming removes the round trip rather than
 * merely making it safe.
 */
export function primeBlob(key, blob) {
  if (key && blob && !blobs.has(key)) blobs.set(key, Promise.resolve(blob));
}

/**
 * The bytes for a clip, wherever they live.
 *
 * Local-first: a clip picked on this device is in IndexedDB and needs no
 * network at all, even for a project that has since been saved to the cloud.
 * The signed URL is the fallback for a project opened on a different machine.
 */
async function resolveBlob(P) {
  const key = P.blobId || P.assetId || P.src;
  if (!key) return null;
  if (blobs.has(key)) return blobs.get(key);

  const p = (async () => {
    if (P.blobId) {
      const local = await getBlob(P.blobId);
      if (local) return local;
    }
    if (P.src) return (await fetch(P.src)).blob();
    return null;
  })();
  blobs.set(key, p);
  return p;
}

/* ── the reader ─────────────────────────────────────────────────────────────
   A factory rather than a class, matching the rest of the codebase: `r` holds
   the state and every function closes over it, which also sidesteps the `this`
   rebinding a dozen async callbacks would otherwise need. */

/* THE CLIP'S TEXTURE, told to three.js as a VIDEO texture.

   A CanvasTexture flagged sRGB is stored as SRGB8_ALPHA8, and in Chrome every
   route into that format from a canvas, an ImageBitmap or a decoder frame is
   slow. Measured on a 1080x1920 frame, waiting for the GPU:

       RGBA8         <- canvas        0.2 ms
       SRGB8_ALPHA8  <- canvas       25.9 ms      (ImageBitmap 20.5, VideoFrame 29.3)

   At 60 fps the whole frame is 16.7 ms, so playback could not keep up, and the
   upload held the GPU so long that the decoder stalled behind it as well: 125 of
   360 frames reached the screen, in freezes of up to 21 frames. With the clip
   hidden, so nothing was uploaded, the same decoder delivered 338 of 360.

   three.js has a path for exactly this (its issue #26516). A texture with
   `isVideoTexture` is stored as plain RGBA8 and decoded from sRGB inside the
   fragment shader (DECODE_VIDEO_TEXTURE), so the colours are unchanged and the
   upload is the fast kind. THREE.VideoTexture itself wants a <video> element and
   calls update() every render; this keeps the canvas we own and gives it the
   same flag, with an update() that does nothing because blit() already marks the
   texture changed exactly when a new frame lands.

   One difference to know about: the hardware filters in linear light, the shader
   decode filters the stored values first. It only shows where a frame is
   magnified or minified across a hard edge; see the colour check in verify.mjs. */
class ClipTexture extends THREE.CanvasTexture {
  constructor(canvas) {
    super(canvas);
    this.isVideoTexture = true;
    this.update = () => {};            // blit() sets needsUpdate when a frame has landed
  }
}

function makeReader(objId) {
  /* OUR canvas, not the sink's. CanvasSink either allocates a fresh canvas per
     frame or recycles a small pool round-robin, so a CanvasTexture pointed at
     one would show a different frame the moment the pool wrapped. Blitting
     into a canvas we own costs a sub-millisecond drawImage and keeps one
     stable GPU texture for the object's whole life. */
  const canvas = document.createElement('canvas');
  canvas.width = 16; canvas.height = 9;
  /* ALPHA ALWAYS, and cleared rather than filled black.

     This context was `{ alpha: false }`, which threw the channel away before
     anything downstream could see it — a transparent clip decoded perfectly
     and arrived as a black rectangle. It costs nothing to keep: an opaque
     clip decodes to alpha 255 everywhere, and the material only turns on
     blending when the file actually carries a matte (scene/objects.js).

     Cleared, not painted, for the same reason. Black is what a video that has
     not opened yet used to look like; on a transparent clip it is a black
     card that flashes before the first frame. */
  const ctx = canvas.getContext('2d', { alpha: true });
  ctx.clearRect(0, 0, 16, 9);

  const tex = new ClipTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.minFilter = THREE.LinearFilter;      // a video frame has no mipmaps
  tex.magFilter = THREE.LinearFilter;
  tex.generateMipmaps = false;
  /* Stated rather than left to the default, because on a BEVELLED panel it is
     load-bearing: ExtrudeGeometry grows the silhouette outward by bevelSize,
     so the bevel ring's UVs sit just outside 0..1 and clamping is what carries
     the edge pixels around the chamfer. Repeat wrapping would mirror the far
     side of the picture onto that rim instead. */
  tex.wrapS = tex.wrapT = THREE.ClampToEdgeWrapping;

  const r = {
    id: objId, ready: false, error: null, aspect: 16 / 9,
    canvas, ctx, tex,
    input: null, sink: null,
    held: null,        // {timestamp, duration} of the frame currently on the canvas
    wanted: null,      // the instant most recently asked for
    inFlight: false,
    pass: null,        // the export generator, when one is open
    cruise: null,      // the playback look-ahead, when one is open
    /* Counted so a test can assert the look-ahead is actually being used, and
       that pausing turns it off. Speed is not a stable thing to assert; which
       path ran is. */
    stats: { seeks: 0, cruised: 0, windows: 0 },
    opening: null
  };

  /* How long a frame is on screen. A frame can arrive with NO duration — a WebM
     block at a cluster boundary has none, and the container may not know the last
     one's — and a frame that lasts 0 s covers no instant at all. Left like that,
     asking for an instant inside it skipped it and returned the NEXT frame, a frame
     early (found by playing a three-second clip through: frame 59 of 90, which the
     0.75 s windows had never reached). Such a frame is taken to last one nominal
     frame of the clip. */
  r.step = 1 / 30;
  const lasts = f => (f.duration > 1e-6 ? f.duration : r.step);

  /** True when the frame already on the canvas covers this instant. */
  const holds = srcT => {
    const h = r.held;
    return !!h && srcT >= h.timestamp - 1e-9 && srcT < h.timestamp + h.duration;
  };

  /* The ONE place the texture's pixels change — so it is also the one place
     that has to ask for a redraw, rather than leaving that to each caller.
     The render loop is strictly on-demand (scene/renderer.js: `if
     (consumeRender()) draw()`), so a blit whose render request is missed is
     simply invisible: the frame sits decoded on the canvas, uploaded to the
     GPU, and nothing draws it until something unrelated happens to request a
     render. Importing a second clip is exactly such a thing, which is what
     made this look like "the first one is black and the second is fine".

     It used to be requested by frameAt's wrapper only — so settle() never
     requested one at all, and any path that threw before reaching it lost the
     request silently. Pairing the two here makes that class of bug
     unreachable rather than merely unlikely. */
  const blit = wrapped => {
    if (!wrapped) return;
    /* Cleared first. With no alpha the new frame covered the old one
       completely; with alpha it does not, and the previous frame shows through
       every transparent pixel — a smear of everything the clip has played. */
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(wrapped.canvas, 0, 0, canvas.width, canvas.height);
    r.held = { timestamp: wrapped.timestamp, duration: lasts(wrapped) };
    tex.needsUpdate = true;
    requestRender();
  };

  /** Parse the container and open a sink. Idempotent and safe to race. */
  r.open = P => {
    if (r.opening) return r.opening;
    r.opening = (async () => {
      try {
        const blob = await resolveBlob(P);
        if (!blob) throw new Error('the clip is not stored on this device');
        const { Input, BlobSource, MP4, QTFF, MATROSKA, WEBM, CanvasSink } = await lib();
        const input = new Input({ formats: [MP4, QTFF, MATROSKA, WEBM],
                                  source: new BlobSource(blob) });
        const track = await input.getPrimaryVideoTrack();
        if (!track) throw new Error('that file has no video track');

        const w = await track.getDisplayWidth(), h = await track.getDisplayHeight();
        r.aspect = (w / h) || 16 / 9;
        const outW = Math.min(w, MAX_DECODE_W);
        const outH = Math.max(2, Math.round(outW / r.aspect));
        const resized = canvas.width !== outW || canvas.height !== outH;
        canvas.width = outW; canvas.height = outH;
        /* THE TEXTURE HAS TO BE THROWN AWAY WITH THE OLD SIZE.

           The canvas is born 16x9 — a placeholder, because the real dimensions
           are not known until the container has been parsed, which is what this
           function is doing. The CanvasTexture was allocated against THAT, and
           three then uploads later frames as a sub-copy into the texture it
           already has. Copying a 1920x1080 canvas into a 16x9 texture fails:

             GL_INVALID_VALUE: glCopySubTextureCHROMIUM:
             Offset overflows texture dimensions.

           and because it fails, the texture keeps what was uploaded first —
           the blank placeholder. Every frame decodes correctly, lands on the
           canvas correctly, and never reaches the GPU.

           Disposing makes three allocate a fresh texture at the new size on the
           next render. Cheap: once per clip, at open. */
        if(resized) tex.dispose();

        r.input = input;
        /* `fit` is REQUIRED once both dimensions are given — the sink refuses
           to guess how to reconcile them with the source. 'fill' is exact
           here rather than a stretch, because outH was derived from the
           track's own display aspect a few lines up. */
        /* `alpha` asked of the sink, which is what makes it merge the matte
           into the canvas rather than compositing the clip onto black. Only
           when the file has one: the merge is CPU work in a worker pool, and
           an opaque clip should not pay for it. */
        r.sink = new CanvasSink(track, { width: outW, height: outH, fit: 'fill',
                                         poolSize: 2, alpha: !!P.srcAlpha });
        r.ready = true;
        /* Whatever instant was asked for while this was opening is still the
           one wanted — otherwise a clip added mid-scrub shows frame zero. */
        if (r.wanted != null) r.frameAt(r.wanted);
        requestRender();
      } catch (err) {
        r.error = err;
        /* Said out loud, not logged. Everything about a clip that fails to
           open still looks right — the object is in the scene, the inspector
           reads its duration and dimensions, the material has its texture —
           and the only symptom is a black rectangle, which reads as a broken
           app rather than a missing file. A console warning is not enough for
           something with no other visible explanation. */
        console.warn('[illusio] could not open clip', err);
        toast(`That clip could not be opened — ${err.message || err}.`, true);
      }
    })();
    return r.opening;
  };

  /* How far ahead to open a window: as far as the list allows, to the end of the
     clip when that is nearer.

     This was 0.75 s, on the reasoning that a short window throws little away
     when it is closed. That cost was never real — the decoder only runs as far
     ahead as backpressure lets it, so a long window holds no more than a short
     one — and the OPENING cost is large: a window starts by seeking back to the
     previous key frame and replaying forward to the instant, which on a clip
     with a two-second key-frame interval is up to ~100 frames. Measured on a
     1080x1920 60 fps clip, the first frame of a window took up to 350 ms (the
     median frame takes 0.6). With a window every 0.75 s that was a freeze, then
     a jump of several frames to catch up, four times in every three seconds of
     playback: the jitter. One window now covers up to CRUISE_MAX frames. */
  const CRUISE_MAX = 512;

  const closeCruise = () => {
    if (!r.cruise) return;
    try { r.cruise.gen.return?.(); } catch (_) {}   // let the decoder go
    r.cruise = null;
  };
  r.closeCruise = closeCruise;

  const openCruise = (t, P) => {
    /* Stepped at the CLIP's own frame rate, cached at import: those are the
       instants the file actually has, so every frame in the window is one
       decode and none is asked for twice. */
    const fps = Math.min(240, Math.max(1, +P?.srcFps || 30));
    const step = 1 / fps;
    // To the end of the clip when its length is known: asking past it only yields the last frame again.
    const dur = +P?.srcDur;
    const end = dur > 0 ? Math.max(t, dur - step) : t + 0.75;
    const times = [];
    for (let x = t; x <= end + 1e-9 && times.length < CRUISE_MAX; x += step) times.push(x);
    if (times.length < 2) return null;
    r.stats.windows++;                  // counted: each one starts with a seek back to a key frame
    r.cruise = { gen: r.sink.canvasesAtTimestamps(times), at: t, tEnd: times[times.length - 1] };
    return r.cruise;
  };

  /** One frame for `t` — from the window when the rule above allows, else a seek. */
  const grab = async (t, P) => {
    if (!S.playing) { closeCruise(); r.stats.seeks++; return r.sink.getCanvas(t); }
    let c = r.cruise;
    // THE rule. Backwards (a loop wrap) or past the end (a scrub) fails it.
    if (c && (t < c.at - 1e-9 || t > c.tEnd + 1e-9)) { closeCruise(); c = null; }
    if (!c) c = openCruise(t, P);
    if (!c) { r.stats.seeks++; return r.sink.getCanvas(t); }
    /* Pull until a frame COVERS the instant rather than assuming one call gives
       it: a busy machine skips frames, and the rule for which frame belongs at
       an instant is the same one getCanvas applies — the last whose start is at
       or before it. Consuming the skipped ones costs nothing; they are already
       decoded. */
    for (;;) {
      const { value, done } = await c.gen.next();
      if (done || !value) { closeCruise(); r.stats.seeks++; return r.sink.getCanvas(t); }
      c.at = value.timestamp;
      if (t < value.timestamp + lasts(value)) { r.stats.cruised++; return value; }
    }
  };

  /**
   * Ask for an instant without waiting for it — the preview path.
   *
   * Coalesced and last-write-wins: a scrub fires this on every pointermove, and
   * decoding every intermediate position would queue work faster than it
   * drains. One decode is in flight at a time; whatever was asked for last
   * while it ran is what gets decoded next, and everything between is dropped.
   * The on-demand render loop is untouched — a finished decode calls
   * requestRender(), nothing polls.
   */
  r.frameAt = (srcT, P) => {
    r.wanted = srcT;
    r.step = 1 / Math.min(240, Math.max(1, +P?.srcFps || 30));
    if (!r.ready || r.pass || holds(srcT)) return;
    if (r.inFlight) return;
    r.inFlight = true;
    (async () => {
      try {
        while (r.wanted != null && !holds(r.wanted) && !r.pass) {
          const t = r.wanted;
          const wrapped = await grab(t, P);
          if (r.pass) break;                  // an export opened mid-flight
          blit(wrapped);
          if (r.wanted === t) break;          // nothing newer arrived
        }
        requestRender();
      } catch (err) {
        console.warn('[illusio] frame decode failed', err);
      } finally {
        r.inFlight = false;
      }
    })();
  };

  /** Await an instant — the still export, which cannot render before it lands. */
  r.settle = async (srcT, P) => {
    r.wanted = srcT;
    closeCruise();                      // an export owns the decoder outright
    if (!r.ready) await r.open(P);
    if (!r.ready || holds(srcT)) return;
    try { blit(await r.sink.getCanvas(srcT)); }
    catch (err) { console.warn('[illusio] frame decode failed', err); }
  };

  /* ── export ───────────────────────────────────────────────────────────────
     The instants are all known up front, which is the only way this API can be
     used: fed a lazy pull-one-await-one iterator it deadlocks outright —
     nothing reaches its sample queue until the key packet changes or the
     iterator ends, so the consumer waits on a frame while the pump waits on
     the next timestamp. Verified directly against mediabunny before any of
     this was built on it. Handed the whole array it streams from the first
     frame and stays memory-bounded, because its own decode loop applies
     backpressure. */

  r.beginPass = times => {
    if (!r.ready) return;
    closeCruise();                      // the pass owns the decoder from here
    r.pass = r.sink.canvasesAtTimestamps(times);
  };

  r.passNext = async () => {
    if (!r.pass) return;
    const res = await r.pass.next();
    if (!res.done) blit(res.value);
  };

  r.endPass = async () => {
    const p = r.pass;
    r.pass = null;
    r.held = null;                  // the preview must re-decode, not trust this
    if (p) { try { await p.return?.(); } catch (_) {} }
  };

  r.dispose = () => {
    r.pass = null;
    closeCruise();                    // and let go of the decoder it was holding
    tex.dispose();
    try { r.input?.dispose?.(); } catch (_) {}
  };

  return r;
}

/* ── the registry ─────────────────────────────────────────────────────────── */

const readers = new Map();          // object id → VideoReader

function readerFor(o) {
  let r = readers.get(o.id);
  if (!r) {
    if (readers.size >= MAX_VIDEOS) return null;
    r = makeReader(o.id);
    readers.set(o.id, r);
    /* Seed the wanted instant from where the playhead already is, BEFORE
       opening. Adding an object does not move the playhead and does not run
       applyAnimation (see addObject in scene/objects.js), so nothing would
       otherwise ever ask this reader for a frame — and the plane would sit on
       the black canvas the reader is born with until someone happened to
       scrub. That is the state a freshly added clip was stuck in: textured,
       correct in every respect, and black. */
    r.wanted = sourceTime(o.params, S.playhead);
    r.open(o.params);
  }
  return r;
}

/**
 * The texture for a video object — the getTexture() analogue.
 *
 * Returns the same texture across node rebuilds, which is what makes dragging
 * the width slider smooth instead of a strobe of black planes.
 */
export function getVideoTexture(o) {
  const r = readerFor(o);
  if (!r) return null;
  return { tex: r.tex, aspect: r.aspect, ready: r.ready, error: r.error };
}

/** How many decoders are open. For the "too many clips" check, and for tests. */
export const videoReaderCount = () => readers.size;

/**
 * The live reader for an object, or null.
 *
 * Exists so a test can read back the actual pixels on the canvas and assert
 * WHICH FRAME is showing — the one claim this whole feature makes. Asserting
 * that a texture was updated would prove nothing about whether it holds the
 * right moment, which is the only thing that matters.
 */
export const peekVideoReader = id => readers.get(id) || null;

/**
 * Put a video object's texture at a source instant.
 *
 * Called from applyAnimation, which is the single funnel every path goes
 * through — see core/video-time.js on why there is exactly one call site.
 * Fire-and-forget: the preview catches up a frame later, and an export uses
 * settleVideos() below, which waits.
 */
export function syncVideoTime(o, srcT) {
  const r = readers.get(o.id) || readerFor(o);
  if (!r) return;
  if (r.pass) return;                 // an export pass drives its own frames
  if (!r.ready) { r.wanted = srcT; return; }
  r.frameAt(srcT, o.params);          // srcFps sizes the look-ahead window
}

/**
 * Drop ONE object's reader, keeping the object.
 *
 * For swapping a clip's file. The registry is keyed on object id and the
 * reader holds the source it was opened with, so an object whose blobId
 * changed under it would go on decoding the old footage forever — reap only
 * collects readers whose object has gone away, which this one has not.
 */
export function dropVideoReader(id) {
  const r = readers.get(id);
  if (!r) return false;
  r.dispose();
  readers.delete(id);
  return true;
}

/** Drop readers whose object is gone — after a delete, an undo, or a load. */
export function reapVideoReaders() {
  const live = new Set();
  for (const o of S.objects) if (o.type === 'video') live.add(o.id);
  for (const [id, r] of readers)
    if (!live.has(id)) { r.dispose(); readers.delete(id); }
}

/** Drop every reader — a project close, where nothing survives. */
export function disposeAllVideoReaders() {
  for (const r of readers.values()) r.dispose();
  readers.clear();
}

/* ── the export pass ──────────────────────────────────────────────────────── */

let activePass = null;

/**
 * Open a decode pass over the exact instants an export will render.
 *
 * @param {number[]} instants  scene times, in the order they will be rendered
 */
export async function beginVideoPass(instants) {
  const vids = S.objects.filter(o => o.type === 'video');
  if (!vids.length) return { end: async () => {} };

  for (const o of vids) {
    const r = readerFor(o);
    if (!r) continue;
    await r.open(o.params);
    r.beginPass(instants.map(t => sourceTime(o.params, t)));
  }
  /* The cursor exists to catch a desync between the instants an export said
     it would render and the ones it actually asks for. Without it a mismatch
     is a video that is silently one frame off for the whole clip — the kind
     of defect nobody notices until a client does. */
  activePass = { instants, cursor: 0, ids: vids.map(o => o.id) };
  return {
    end: async () => {
      activePass = null;
      for (const id of readers.keys()) await readers.get(id).endPass();
    }
  };
}

/**
 * Make every video object's texture hold the frame for scene time `t`.
 *
 * Resolves immediately when the scene has no video, so a scene without one
 * pays a Map lookup and nothing else.
 */
export async function settleVideos(t) {
  if (!readers.size) return;

  if (activePass) {
    const expected = activePass.instants[activePass.cursor];
    if (expected === undefined || Math.abs(expected - t) > 1e-9)
      throw new Error(
        `Video export desynchronised: frame ${activePass.cursor} was rendered at ${t}s ` +
        `but the decode pass was built for ${expected}s.`);
    activePass.cursor++;
    for (const id of activePass.ids) await readers.get(id)?.passNext();
    return;
  }

  for (const r of readers.values()) {
    const o = S.objects.find(x => x.id === r.id);
    if (o) await r.settle(sourceTime(o.params, t), o.params);
  }
}
